import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Loader2, Paperclip, Save, FileCheck2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { ProfessionalStatusBadge } from '@/components/professional/ProfessionalCard';

const FIELDS = [
  { key: 'professional_name', label: 'Nome profissional', required: true, placeholder: 'Ex.: Dra. Ana Souza' },
  { key: 'registry', label: 'Registro profissional (CRP, CRM, CRESS etc.)', placeholder: 'Ex.: CRP 06/12345' },
  { key: 'institution', label: 'Instituição', placeholder: 'Onde você atua ou se formou' },
  { key: 'specialty', label: 'Especialidade', placeholder: 'Ex.: Dependência química' },
];

export default function ProfessionalCardPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({ professional_name: '', registry: '', institution: '', specialty: '', short_cv: '', visibility: 'private' });
  const [card, setCard] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return;
    api.get(`/user/${user.id}/professional`).then(({ data }) => {
      setCard(data);
      setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(data).filter(([k, v]) => k in f && v != null)) }));
    }).catch(() => {});
  }, [user?.id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/user/${user.id}/professional`, form);
      setCard(data);
      toast.success(data.status === 'pending' ? 'Dados enviados! Seu perfil está em análise.' : 'Perfil profissional salvo');
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Não foi possível salvar');
    } finally { setSaving(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/user/${user.id}/professional/certificate`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCard(data);
      toast.success('Certificado anexado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar o arquivo');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const hasSaved = card && card.status !== 'none';

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="professional-card-page">
      <Link to="/me" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="professional-back-link">
        <ArrowLeft className="h-4 w-4" /> Voltar ao perfil
      </Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold flex-1">Perfil Profissional</h1>
        {hasSaved && <ProfessionalStatusBadge status={card.status} />}
      </div>
      {card?.status === 'rejected' && card.reject_reason && (
        <p className="text-sm text-red-500 bg-red-500/10 rounded-lg p-3" data-testid="professional-reject-reason">Motivo: {card.reject_reason}</p>
      )}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <form onSubmit={save} className="space-y-4">
            {FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`pro-${f.key}`}>{f.label}</Label>
                <Input id={`pro-${f.key}`} value={form[f.key]} onChange={set(f.key)} required={f.required} placeholder={f.placeholder} className="min-h-[44px]" data-testid={`pro-input-${f.key}`} />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label htmlFor="pro-short_cv">Mini-bio</Label>
              <Textarea id="pro-short_cv" value={form.short_cv} onChange={set('short_cv')} maxLength={1500} rows={4} placeholder="Sua formação e experiência em poucas linhas" data-testid="pro-input-short_cv" />
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
              <div>
                <Label htmlFor="pro-visibility">Perfil público</Label>
                <p className="text-xs text-muted-foreground">Quando aprovado, outras pessoas da comunidade podem ver.</p>
              </div>
              <Switch id="pro-visibility" checked={form.visibility === 'public'} onCheckedChange={(v) => setForm((f) => ({ ...f, visibility: v ? 'public' : 'private' }))} data-testid="pro-visibility-switch" />
            </div>
            <Button type="submit" disabled={saving} className="w-full min-h-[48px] gap-2" data-testid="pro-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar e enviar para validação
            </Button>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 sm:p-6 space-y-3">
          <p className="font-semibold">Currículo ou certificado</p>
          <p className="text-sm text-muted-foreground">PDF, JPG ou PNG até 10MB. Só você e a equipe de validação veem este arquivo.</p>
          {card?.has_certificate && (
            <p className="text-sm flex items-center gap-2 text-green-600" data-testid="pro-certificate-name"><FileCheck2 className="h-4 w-4" /> {card.certificate_name}</p>
          )}
          <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={upload} data-testid="pro-certificate-input" />
          <Button type="button" variant="outline" disabled={!hasSaved || uploading} onClick={() => fileRef.current?.click()} className="min-h-[44px] gap-2" data-testid="pro-certificate-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            {card?.has_certificate ? 'Trocar arquivo' : 'Anexar arquivo'}
          </Button>
          {!hasSaved && <p className="text-xs text-muted-foreground">Salve os dados acima antes de anexar o arquivo.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
